import React, { Fragment, useState, useEffect, useRef } from "react";
import Breadcrumb from "../../common/breadcrumb";
import MenuItem from "@mui/material/MenuItem";
import { Card, CardBody, Container, Row } from "reactstrap";
import { Button, Checkbox, Typography } from "@mui/material";
import { Grid, TextField } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import {
  PersonSearch,
  Refresh,
  Send,
  Edit,
  Save,
  Cancel,
  UploadFile,
  SimCardDownloadRounded,
} from "@mui/icons-material";
import sampleBulkInviteFile from "../../../../src/assets/Sample_bulk_invite.xlsx";
import {
  parseBulkInviteExcel,
  isImportedRow,
} from "./bulkInviteImportUtils";
import { makeStyles } from "@mui/styles";
import { IconButton } from "@mui/material";
import CommonAccordion from "../../common/commonAccordion";
import CommonTableApBulkInvite from "../../common/commonTableApBulkInvite";
import {
  getAllVendorType,
  getVendorsByType,
  sendBulkInvite,
  updateVendorContactDetails,
} from "../../api/ap_services";
import CommonLoader from "../../common/commonLoader";
import { useNavigate } from "react-router-dom";
import {
  createBulkInviteTableHeader,
  createBulkInviteTableData,
} from "../../common/commonConstantAp";

const INVITE_TYPE_MANUAL = "MANUAL_PROCESSING";
const INVITE_TYPE_IMPORT = "IMPORT_INVITE";

const useStyles = makeStyles((theme) => ({
  btnClass: {
    padding: "0.5rem 1.5rem!important",
    fontWeight: 600,
  },
  btnJust: {
    display: "flex!important",
    justifyContent: "end!important",
  },
  cardbody: {
    padding: `${theme.spacing(1, 1.3)} !important`,
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(0)} !important`,
      margin: `${theme.spacing(0, 1)} !important`,
    },
  },
  muiGrid: {
    marginTop: "-2vh",
    [theme.breakpoints.down("sm")]: {
      lineHeight: 5,
    },
  },
}));

const CreateBulkInvite = () => {
  const history = useNavigate();
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [selectedVendorType, setSelectedVendorType] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [inviteType, setInviteType] = useState(INVITE_TYPE_MANUAL);
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);

  const isManualProcessing = inviteType === INVITE_TYPE_MANUAL;
  const isImportInvite = inviteType === INVITE_TYPE_IMPORT;

  useEffect(() => {
    loadVendorTypes();
  }, []);

  const resetManualProcessingState = () => {
    setSelectedVendorType("");
    setSelectedVendors([]);
    setSearchFilter("");
    setData([]);
    setFilteredData([]);
    setEditingRowId(null);
    setEditedData({});
    setValidationErrors({});
  };

  const handleInviteTypeChange = (event) => {
    const value = event.target.value;
    setInviteType(value);
    resetManualProcessingState();
    setImportFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImportFile(null);
      return;
    }
    setImportFile(file);
  };

  const handleImportExcel = () => {
    if (!importFile) {
      toast.error("Please choose a file first");
      return;
    }

    setLoading(true);
    parseBulkInviteExcel(importFile)
      .then((rows) => {
        setLoading(false);
        if (!rows.length) {
          toast.error("No valid data found in the file");
          return;
        }
        setData(rows);
        setFilteredData(rows);
        setSelectedVendors([]);
        setEditingRowId(null);
        setEditedData({});
        setValidationErrors({});
        toast.success(`Imported ${rows.length} record(s) successfully`);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to import file. Please use a valid .xlsx file");
      });
  };

  const loadVendorTypes = () => {
    setLoading(true);
    getAllVendorType(0, 9999)
      .then((res) => {
        setLoading(false);
        const list = res?.response?.responseObject ?? [];
        setVendorTypes(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        setLoading(false);
        toast.error("Failed to load vendor types");
      });
  };

  const handleVendorTypeChange = (event) => {
    const vendorTypeId = event.target.value;
    setSelectedVendorType(vendorTypeId);
    setSelectedVendors([]);
    setSearchFilter("");

    if (vendorTypeId) {
      setLoading(true);
      getVendorsByType(vendorTypeId)
        .then((res) => {
          setLoading(false);
          const list = res?.response?.responseObject ?? [];
          const vendors = Array.isArray(list) ? list : [];
          setData(vendors);
          setFilteredData(vendors);
        })
        .catch((err) => {
          setLoading(false);
          toast.error("Failed to load vendors");
        });
    } else {
      setData([]);
      setFilteredData([]);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchFilter(value);

    if (!value) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => {
        const vendorCode = (item.vendorCode || item.VENDOR_CODE || "").toLowerCase();
        const vendorName = (item.vendorName || item.VENDOR_NAME || "").toLowerCase();
        const searchLower = value.toLowerCase();
        return vendorCode.includes(searchLower) || vendorName.includes(searchLower);
      });
      setFilteredData(filtered);
    }
  };

  const getVendorRegistrationId = (row) =>
    row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;

  const filteredVendorIds = filteredData.map(getVendorRegistrationId);
  const allFilteredSelected =
    filteredVendorIds.length > 0 &&
    filteredVendorIds.every((id) => selectedVendors.includes(id));
  const someFilteredSelected = filteredVendorIds.some((id) =>
    selectedVendors.includes(id)
  );

  const handleCheckboxChange = (vendorRegistrationId, checked) => {
    if (checked) {
      setSelectedVendors((prev) => [...prev, vendorRegistrationId]);
    } else {
      setSelectedVendors((prev) =>
        prev.filter((id) => id !== vendorRegistrationId)
      );
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedVendors((prev) => [
        ...new Set([...prev, ...filteredVendorIds]),
      ]);
    } else {
      setSelectedVendors((prev) =>
        prev.filter((id) => !filteredVendorIds.includes(id))
      );
    }
  };

  const handleSendInvite = () => {
    if (selectedVendors.length === 0) {
      toast.error("Please select at least one vendor");
      return;
    }

    if (window.confirm(`Send bulk invite to ${selectedVendors.length} vendor(s)?`)) {
      setLoading(true);
  console.log(inviteType,"inviteType1")
      sendBulkInvite(selectedVendors, inviteType)
        .then((res) => {
          setLoading(false);
          if (res.response.responseCode) {
            toast.success("Bulk invite sent successfully");
            setTimeout(() => {
              history(`${process.env.PUBLIC_URL}/master/bulk-invite`);
            }, 1500);
          } else {
            toast.error(res.response.message || "Failed to send bulk invite");
          }
        })
        .catch((err) => {
          setLoading(false);
          toast.error("Error sending bulk invite");
        });
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateContactNumber = (number) => {
    const contactRegex = /^[0-9]{10}$/;
    return contactRegex.test(number);
  };

  const handleEditClick = (row) => {
    const vendorRegistrationId = row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
    setEditingRowId(vendorRegistrationId);
    setEditedData({
      ownerEmail: row.ownerEmail || row.OWNER_EMAIL || "",
      ownerContactNo: row.ownerContactNo || row.OWNER_CONTACT_NO || "",
      primaryEmail: row.primaryEmail || row.PRIMARY_EMAIL || "",
      primaryContact: row.primaryContact || row.PRIMARY_CONTACT || "",
    });
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditedData({});
    setValidationErrors({});
  };

  const handleFieldChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const errors = { ...validationErrors };
    if (field === "ownerEmail" || field === "primaryEmail") {
      if (!validateEmail(value)) {
        errors[field] = "Invalid email format";
      } else {
        delete errors[field];
      }
    } else if (field === "ownerContactNo" || field === "primaryContact") {
      if (!validateContactNumber(value)) {
        errors[field] = "Must be 10 digits";
      } else {
        delete errors[field];
      }
    }
    setValidationErrors(errors);
  };

  const applyLocalContactUpdate = () => {
    const updatedData = data.map((item) => {
      const itemId = item.vendorRegistrationId || item.VENDOR_REGISTRATION_ID;
      if (itemId === editingRowId) {
        return {
          ...item,
          ownerEmail: editedData.ownerEmail,
          OWNER_EMAIL: editedData.ownerEmail,
          ownerContactNo: editedData.ownerContactNo,
          OWNER_CONTACT_NO: editedData.ownerContactNo,
          primaryEmail: editedData.primaryEmail,
          PRIMARY_EMAIL: editedData.primaryEmail,
          primaryContact: editedData.primaryContact,
          PRIMARY_CONTACT: editedData.primaryContact,
        };
      }
      return item;
    });
    setData(updatedData);
    setFilteredData(updatedData);
    setEditingRowId(null);
    setEditedData({});
    setValidationErrors({});
    toast.success("Contact details updated successfully");
  };

  const handleSaveEdit = () => {
    const errors = {};
    if (!validateEmail(editedData.ownerEmail)) {
      errors.ownerEmail = "Invalid email format";
    }
    if (!validateEmail(editedData.primaryEmail)) {
      errors.primaryEmail = "Invalid email format";
    }
    if (!validateContactNumber(editedData.ownerContactNo)) {
      errors.ownerContactNo = "Must be 10 digits";
    }
    if (!validateContactNumber(editedData.primaryContact)) {
      errors.primaryContact = "Must be 10 digits";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error("Please fix validation errors");
      return;
    }

    const editingRow = data.find(
      (item) =>
        (item.vendorRegistrationId || item.VENDOR_REGISTRATION_ID) ===
        editingRowId
    );

    if (isImportedRow(editingRow)) {
      applyLocalContactUpdate();
      return;
    }

    setLoading(true);
    updateVendorContactDetails(
      editingRowId,
      editedData.ownerEmail,
      editedData.ownerContactNo,
      editedData.primaryEmail,
      editedData.primaryContact
    )
      .then((res) => {
        setLoading(false);
        if (res.response.responseCode) {
          applyLocalContactUpdate();
        } else {
          toast.error(res.response.message || "Failed to update contact details");
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error("Error updating contact details");
      });
  };

  const tableColumns = [
    {
      name: "actions",
      label: "Actions",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          const isEditing = editingRowId === vendorRegistrationId;

          return (
            <div style={{ display: "flex", gap: "4px" }}>
              {isEditing ? (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSaveEdit}
                    title="Save"
                  >
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={handleCancelEdit}
                    title="Cancel"
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleEditClick(row)}
                  title="Edit"
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    },
    {
      name: "sendEmail",
      label: "Send Email",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          return (
            <Checkbox
              checked={selectedVendors.includes(vendorRegistrationId)}
              onChange={(e) =>
                handleCheckboxChange(vendorRegistrationId, e.target.checked)
              }
            />
          );
        },
      },
    },
    {
      name: "vendorCode",
      label: "Vendor Code",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          return row.vendorCode || row.VENDOR_CODE || "-";
        },
      },
    },
    {
      name: "vendorName",
      label: "Vendor Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          return row.vendorName || row.VENDOR_NAME || "-";
        },
      },
    },
    {
      name: "brand",
      label: "Brand",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          return row.brand || row.BRAND || "-";
        },
      },
    },
    {
      name: "vendorType",
      label: "Vendor Type",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          return row.vendorType || row.VENDOR_TYPE || "-";
        },
      },
    },
    {
      name: "vendorClassification",
      label: "Vendor Classification",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          return row.vendorClassification || row.VENDOR_CLASSIFICATION || "-";
        },
      },
    },
    {
      name: "ownerEmail",
      label: "Owner Mail ID",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          const isEditing = editingRowId === vendorRegistrationId;
          const currentValue = row.ownerEmail || row.OWNER_EMAIL || "";

          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editedData.ownerEmail || ""}
                onChange={(e) => handleFieldChange("ownerEmail", e.target.value)}
                error={!!validationErrors.ownerEmail}
                helperText={validationErrors.ownerEmail}
                fullWidth
              />
            );
          }
          return currentValue || "-";
        },
      },
    },
    {
      name: "ownerContactNo",
      label: "Owner Contact No",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          const isEditing = editingRowId === vendorRegistrationId;
          const currentValue = row.ownerContactNo || row.OWNER_CONTACT_NO || "";

          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editedData.ownerContactNo || ""}
                onChange={(e) => handleFieldChange("ownerContactNo", e.target.value)}
                error={!!validationErrors.ownerContactNo}
                helperText={validationErrors.ownerContactNo}
                fullWidth
              />
            );
          }
          return currentValue || "-";
        },
      },
    },
    {
      name: "primaryEmail",
      label: "Primary Mail ID",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          const isEditing = editingRowId === vendorRegistrationId;
          const currentValue = row.primaryEmail || row.PRIMARY_EMAIL || "";

          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editedData.primaryEmail || ""}
                onChange={(e) => handleFieldChange("primaryEmail", e.target.value)}
                error={!!validationErrors.primaryEmail}
                helperText={validationErrors.primaryEmail}
                fullWidth
              />
            );
          }
          return currentValue || "-";
        },
      },
    },
    {
      name: "primaryContact",
      label: "Primary Contact",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = filteredData[tableMeta.rowIndex];
          const vendorRegistrationId =
            row.vendorRegistrationId || row.VENDOR_REGISTRATION_ID;
          const isEditing = editingRowId === vendorRegistrationId;
          const currentValue = row.primaryContact || row.PRIMARY_CONTACT || "";

          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editedData.primaryContact || ""}
                onChange={(e) => handleFieldChange("primaryContact", e.target.value)}
                error={!!validationErrors.primaryContact}
                helperText={validationErrors.primaryContact}
                fullWidth
              />
            );
          }
          return currentValue || "-";
        },
      },
    },
  ];

  const showBulkInviteTable =
    (isManualProcessing && selectedVendorType) ||
    (isImportInvite && filteredData.length > 0);

  return (
    <Fragment>
      {loading ? <CommonLoader /> : null}
      <Breadcrumb title="Create Bulk Invite" />
      <Container fluid={true}>
        <Row>
          <Card style={{ marginBottom: 2}}>
            <CardBody className={classes.cardbody} >
              <Grid container spacing={2} className={classes.muiGrid}>
                <Grid item sm={4} xs={12}>
                  <TextField
                    fullWidth
                    label="Invite Type"
                    size="small"
                    select
                    value={inviteType}
                    onChange={handleInviteTypeChange}
                  >
                    <MenuItem value={INVITE_TYPE_MANUAL}>
                      Manual Processing
                    </MenuItem>
                    <MenuItem value={INVITE_TYPE_IMPORT}>
                      Import invite
                    </MenuItem>
                  </TextField>
                </Grid>
                {isManualProcessing && (
                  <Grid item sm={4} xs={12}>
                    <TextField
                      fullWidth
                      label="Vendor Type"
                      size="small"
                      select
                      value={selectedVendorType}
                      onChange={handleVendorTypeChange}
                    >
                      <MenuItem value="">
                        <em>Select Vendor Type</em>
                      </MenuItem>
                      {vendorTypes.map((type) => (
                        <MenuItem
                          key={type.vendorTypeId}
                          value={type.vendorTypeId}
                        >
                          {type.name} ({type.code})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {isImportInvite && (
                  <Grid item sm={8} xs={12}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      style={{ display: "none" }}
                      onChange={handleImportFileChange}
                    />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnClass}
                        startIcon={<UploadFile />}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Choose File
                      </Button>
                      <Button
                        component="a"
                        href={sampleBulkInviteFile}
                        download="Sample_bulk_invite.xlsx"
                        variant="outlined"
                        color="primary"
                        className={classes.btnClass}
                        startIcon={<SimCardDownloadRounded />}
                      >
                        Sample Import
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        className={classes.btnClass}
                        onClick={handleImportExcel}
                        disabled={!importFile}
                      >
                        Import
                      </Button>
                    </div>
                    {importFile ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Selected: {importFile.name}
                      </Typography>
                    ) : null}
                  </Grid>
                )}
              </Grid>
            </CardBody>
          </Card>

          {isManualProcessing && (
            <>
              {/* {isManualProcessing && ( */}
              <CommonAccordion accordionTitle={"Filter"}>
                <Grid container columnSpacing={2} className={classes.muiGrid}>
                  <Grid item sm={4} xs={12}>
                    <TextField
                      fullWidth
                      label="Vendor Code / Name"
                      size="small"
                      value={searchFilter}
                      onChange={handleSearchChange}
                      placeholder="Search by Vendor Code or Name"
                    />
                  </Grid>
                  <Grid
                    item
                    sm={2}
                    xs={12}
                    sx={{
                      margin: "auto",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      color="primary"
                      variant="contained"
                      type="button"
                      className={classes.btnClass}
                      onClick={() => setSearchFilter("")}
                    >
                      <Refresh />
                    </Button>
                  </Grid>
                </Grid>
              </CommonAccordion>
              {/* )} */}

              <Card>
                <CardBody className={classes.cardbody}>
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={
                          someFilteredSelected && !allFilteredSelected
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                      />
                      <span>Select All</span>
                    </div>
                    <Button
                      color="secondary"
                      variant="contained"
                      type="button"
                      className={classes.btnClass}
                      startIcon={<Send />}
                      onClick={handleSendInvite}
                      disabled={selectedVendors.length === 0}
                    >
                      Send Invite ({selectedVendors.length})
                    </Button>
                  </div>
                  <div>
                    <CommonTableApBulkInvite
                      tableData={filteredData}
                      tableHeaders={createBulkInviteTableHeader}
                      tableColumns={tableColumns}
                      displayPagination={false}
                    />
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </Row>
      </Container>
      <ToastContainer autoClose={1000} />
    </Fragment>
  );
};

export default CreateBulkInvite;
